import { useState } from "react";
import { ArrowUpToLine, ArrowDownToLine, ArrowLeftToLine, ArrowRightToLine, Rows3, Columns3, Trash2, PaintBucket, Grid3x3, AlignLeft, AlignCenter, AlignRight, Image as ImageIcon, X, PenLine, Type, CalendarDays, MousePointerClick, Signature, Copy, Eraser } from "lucide-react";
import { RB, ColorBtn, NSelect } from "./parts";
import { tableOp, emit } from "../cmd";

export const TableTools = () => {
  const [scope, setScope] = useState("cell");
  const [shade, setShade] = useState("#E8F1FB");
  const [bc, setBc] = useState("#64748B");
  const [bw, setBw] = useState("1");
  const [bs, setBs] = useState("solid");
  const border = (patch = {}) => tableOp("border", { scope, color: bc, width: bw, style: bs, ...patch });
  return (
    <div className="ctx-bar" data-testid="table-tools">
      <span className="ctx-title"><Grid3x3 className="h-3.5 w-3.5" />Table</span>
      <RB icon={ArrowUpToLine} label="Insert row above" onClick={() => tableOp("rowAbove")} testid="table-row-above" />
      <RB icon={ArrowDownToLine} label="Insert row below" onClick={() => tableOp("rowBelow")} testid="table-row-below" />
      <RB icon={ArrowLeftToLine} label="Insert column left" onClick={() => tableOp("colLeft")} testid="table-col-left" />
      <RB icon={ArrowRightToLine} label="Insert column right" onClick={() => tableOp("colRight")} testid="table-col-right" />
      <RB icon={Rows3} label="Delete row" onClick={() => tableOp("delRow")} testid="table-del-row" />
      <RB icon={Columns3} label="Delete column" onClick={() => tableOp("delCol")} testid="table-del-col" />
      <RB icon={Trash2} label="Delete table" onClick={() => tableOp("delTable")} testid="table-delete" />
      <span className="ctx-sep" />
      <span className="text-xs text-muted-foreground">Apply to</span>
      <NSelect width={86} label="Style scope" testid="table-scope" value={scope} onChange={setScope}
        options={[["cell", "Cell"], ["row", "Row"], ["col", "Column"], ["table", "Table"]].map(([value, label]) => ({ value, label }))} />
      <ColorBtn icon={PaintBucket} label="Shading" value={shade} testid="table-shading" onChange={(v) => { setShade(v); tableOp("shade", { scope, color: v }); }} />
      <RB icon={X} label="Clear shading" onClick={() => tableOp("shade", { scope, color: "" })} testid="table-clear-shading" />
      <span className="ctx-sep" />
      <span className="text-xs text-muted-foreground">Borders</span>
      <NSelect width={84} label="Border style" testid="table-border-style" value={bs} onChange={(v) => { setBs(v); border({ style: v }); }}
        options={["solid", "dashed", "dotted", "double", "none"].map((v) => ({ value: v, label: v[0].toUpperCase() + v.slice(1) }))} />
      <NSelect width={64} label="Border width" testid="table-border-width" value={bw} onChange={(v) => { setBw(v); border({ width: v }); }}
        options={["1", "2", "3", "4"].map((v) => ({ value: v, label: `${v}px` }))} />
      <ColorBtn icon={Grid3x3} label="Border color" value={bc} testid="table-border-color" onChange={(v) => { setBc(v); border({ color: v }); }} />
      <span className="ctx-sep" />
      <RB icon={AlignLeft} label="Cell text left" onClick={() => tableOp("align", { scope, align: "left" })} testid="table-align-left" />
      <RB icon={AlignCenter} label="Cell text center" onClick={() => tableOp("align", { scope, align: "center" })} testid="table-align-center" />
      <RB icon={AlignRight} label="Cell text right" onClick={() => tableOp("align", { scope, align: "right" })} testid="table-align-right" />
    </div>
  );
};

export const ImageTools = ({ img, onDone }) => {
  const setW = (w) => { img.style.width = w; img.style.height = "auto"; emit(); };
  const align = (a) => {
    img.style.display = "block";
    img.style.marginLeft = a === "left" ? "0" : "auto";
    img.style.marginRight = a === "right" ? "0" : "auto";
    emit();
  };
  return (
    <div className="ctx-bar" data-testid="image-tools">
      <span className="ctx-title"><ImageIcon className="h-3.5 w-3.5" />Picture</span>
      <span className="text-xs text-muted-foreground">Width</span>
      {["25%", "50%", "75%", "100%"].map((w) => <RB key={w} text={w} label={`Width ${w}`} onClick={() => setW(w)} testid={`image-width-${w.replace("%", "")}`} />)}
      <span className="ctx-sep" />
      <RB icon={AlignLeft} label="Align left" onClick={() => align("left")} testid="image-align-left" />
      <RB icon={AlignCenter} label="Center" onClick={() => align("center")} testid="image-align-center" />
      <RB icon={AlignRight} label="Align right" onClick={() => align("right")} testid="image-align-right" />
      <span className="ctx-sep" />
      <RB icon={Trash2} label="Delete picture" onClick={() => { img.remove(); emit(); onDone(); }} testid="image-delete" />
    </div>
  );
};

export const PdfTools = ({ page, onPlace, onCancel }) => (
  <div className="ctx-bar" data-testid="pdf-tools">
    <span className="ctx-title"><MousePointerClick className="h-3.5 w-3.5" />PDF page {page}</span>
    <span className="text-xs text-muted-foreground mr-2">Add at the marked point:</span>
    <RB icon={PenLine} text="Signature" label="Add signature here" onClick={() => onPlace("sig")} testid="pdf-place-signature" />
    <RB icon={Signature} text="Initials" label="Add initials here" onClick={() => onPlace("initials")} testid="pdf-place-initials" />
    <RB icon={Copy} text="Initials on every page" label="Add initials at this spot on every page" onClick={() => onPlace("initials-all")} testid="pdf-place-initials-all" />
    <RB icon={Eraser} text="Whiteout" label="Cover original content with a white box" onClick={() => onPlace("box")} testid="pdf-place-whiteout" />
    <RB icon={Type} text="Text" label="Add text here" onClick={() => onPlace("text")} testid="pdf-place-text" />
    <RB icon={CalendarDays} text="Today's date" label="Add date here" onClick={() => onPlace("date")} testid="pdf-place-date" />
    <span className="ctx-sep" />
    <RB icon={X} text="Cancel" label="Cancel" onClick={onCancel} testid="pdf-place-cancel" />
    <span className="text-[11px] text-muted-foreground ml-3 hidden lg:inline">Tip: hover a placed item and drag ⠿ to move it, × to remove.</span>
  </div>
);
